import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NormalSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import { EntityMeta, Item, UserApproval } from '@qtek/shared/models';
import {
  catchError,
  filter,
  map,
  mergeMap,
  of,
  pipe,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { ItemService } from './item.service';

export interface ItemsState {
  allItems: Record<string, Item[]>;
  itemsMeta: EntityMeta;
  itemsLoading: Record<string, boolean>;
}

const initialState: ItemsState = {
  allItems: {},
  itemsMeta: new EntityMeta(),
  itemsLoading: {},
};

export const ItemStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    update$: new Subject<{ res: Item; mysid: string }>(),
    updateFailure$: new Subject<{ mysid?: string }>(),
    create$: new Subject<{ res: Item; mysid: string }>(),
    createFailure$: new Subject<unknown>(),
    getItem$: new Subject<{ res: Item; mysid: string }>(),
    getItemFailure$: new Subject<unknown>(),
    getItems$: new Subject<{ res: Item[]; meta: EntityMeta; mysid: string }>(),
    getItemsFailure$: new Subject<unknown>(),
    sendNdaEvent$: new Subject<unknown>(),
    sendNdaEventFailure$: new Subject<unknown>(),
  })),
  withMethods(
    (
      store,
      service = inject(ItemService),
      snackBar = inject(SnackBarService)
    ) => {
      const disconnect$ = new Subject<string>();
      const pendingGetMysidById = new Map<string, string>();

      return {
        connect: rxMethod<any>(
          pipe(
            mergeMap((payload: any) => {
              patchState(store, state => ({
                itemsLoading: { ...state.itemsLoading, [payload.mysid]: true },
              }));
              const messageFilter =
                payload.mysid === 'task_form_related_deal_lookup'
                  ? (message: any) =>
                      message.ent === service.entityName &&
                      Boolean(message.op) &&
                      (message.mysid === payload.mysid || message.op === 'get')
                  : undefined;

              return service.subscribe(payload, messageFilter).pipe(
                takeUntil(disconnect$.pipe(filter(id => id === payload.mysid))),
                tap((data: any) => {
                  if (!data.op) return;
                  const { res, op, sts, mysid } = data;

                  switch (op) {
                    case 'query':
                    case 'fast_query': {
                      if (mysid === 'task_form_related_deal_lookup') {
                        patchState(store, state => ({
                          itemsLoading: {
                            ...state.itemsLoading,
                            [mysid]: false,
                          },
                        }));
                        return;
                      }
                      if (res) {
                        patchState(store, state => ({
                          allItems: { ...state.allItems, [mysid]: res ?? [] },
                          itemsMeta: sts?.meta ?? state.itemsMeta,
                          itemsLoading: {
                            ...state.itemsLoading,
                            [mysid]: false,
                          },
                        }));
                        store.getItems$.next({
                          res: res ?? [],
                          meta: sts?.meta,
                          mysid,
                        });
                      } else {
                        patchState(store, state => ({
                          itemsLoading: {
                            ...state.itemsLoading,
                            [mysid]: false,
                          },
                        }));
                        store.getItemsFailure$.next(sts);
                      }
                      break;
                    }
                    case 'get': {
                      const item = Array.isArray(res) ? res[0] : res;
                      const responseId = data.id ?? item?._id;
                      const mysidFromPending =
                        responseId && pendingGetMysidById.get(responseId);
                      if (responseId) pendingGetMysidById.delete(responseId);
                      const mysidFinal = mysidFromPending ?? mysid;

                      if (item && mysidFinal) {
                        patchState(store, state => {
                          const existing = state.allItems[mysidFinal] ?? [];
                          const idx = existing.findIndex(
                            i => i._id === item._id
                          );
                          const updated =
                            idx >= 0
                              ? existing.map((i, j) => (j === idx ? item : i))
                              : [...existing, item];
                          return {
                            allItems: {
                              ...state.allItems,
                              [mysidFinal]: updated,
                            },
                          };
                        });
                        store.getItem$.next({ res: item, mysid: mysidFinal });
                      } else if (!item) {
                        store.getItemFailure$.next(sts);
                      }
                      break;
                    }
                    case 'ins': {
                      if (res) {
                        patchState(store, state => ({
                          allItems: {
                            ...state.allItems,
                            [mysid]: [...(state.allItems[mysid] ?? []), res],
                          },
                        }));
                        store.create$.next({ res, mysid });
                        snackBar.showSnackBar({
                          type: 'NormalMessage',
                          message: 'GNR_ITM_CREATED',
                        } as NormalSnackBarData);
                      } else {
                        store.createFailure$.next(sts);
                      }
                      break;
                    }
                    case 'upd': {
                      if (res) {
                        patchState(store, state => {
                          const arr = (state.allItems[mysid] ?? []).slice();
                          const idx = arr.findIndex(i => i._id === res._id);
                          if (idx >= 0) {
                            const merged = { ...arr[idx], ...res };
                            if ('pid' in arr[idx] && !('pid' in res)) {
                              delete (merged as any).pid;
                            }
                            arr[idx] = merged;
                          }
                          return {
                            allItems: { ...state.allItems, [mysid]: arr },
                          };
                        });
                        store.update$.next({ res, mysid });
                        snackBar.showSnackBar({
                          type: 'NormalMessage',
                          message: 'GNR_ITM_UPDATED',
                        } as NormalSnackBarData);
                      } else {
                        store.updateFailure$.next({ mysid });
                      }
                      break;
                    }
                    case 'del': {
                      if (res) {
                        patchState(store, state => ({
                          allItems: {
                            ...state.allItems,
                            [mysid]: (state.allItems[mysid] ?? []).filter(
                              i => i._id !== res._id
                            ),
                          },
                        }));
                        snackBar.showSnackBar({
                          type: 'NormalMessage',
                          message: 'GNR_ITM_DELETED',
                        } as NormalSnackBarData);
                      }
                      break;
                    }
                  }
                })
              );
            })
          )
        ),

        disconnect(mysid: string): void {
          disconnect$.next(mysid);
          patchState(store, state => {
            const { [mysid]: _a, ...allItems } = state.allItems;
            const { [mysid]: _b, ...itemsLoading } = state.itemsLoading;
            return { allItems, itemsLoading };
          });
        },

        getItems(payload: Parameters<typeof service.getItems>[0]): void {
          service.getItems(payload);
        },

        getItemById(payload: {
          id: string;
          view?: string;
          mysid?: string;
        }): void {
          if (payload.id && payload.mysid) {
            pendingGetMysidById.set(payload.id, payload.mysid);
          }
          service.getItemById(payload);
        },

        createItem(payload: Item): void {
          service.createItem({ res: payload });
        },

        update(payload: { payload: Item; id: string; mysid?: string }): void {
          service.updateItem({
            res: payload.payload,
            id: payload.id,
            mysid: payload.mysid,
          });
        },

        deleteItem(payload: number | string): void {
          service.deleteItem({ id: payload });
        },

        mintItem: rxMethod<number>(
          pipe(
            switchMap(seqno =>
              service.mintItem(seqno).pipe(catchError(() => of(null)))
            )
          )
        ),

        sendNdaEvent: rxMethod<{ parId: string; userApproval: UserApproval }>(
          pipe(
            switchMap(({ parId, userApproval }) =>
              service.sendNdaEvent(parId, userApproval).pipe(
                map(({ res }) => {
                  store.sendNdaEvent$.next(res);
                  return res;
                }),
                catchError(({ sts }) => {
                  store.sendNdaEventFailure$.next(sts);
                  return of(null);
                })
              )
            )
          )
        ),

        itemsByMysid(mysid: string) {
          return computed(() => store.allItems()[mysid] ?? []);
        },

        loadingByMysid(mysid: string) {
          return computed(() => store.itemsLoading()[mysid] ?? false);
        },
      };
    }
  ),
  withComputed(({ itemsMeta }) => ({
    currentItemsMeta: computed(() => itemsMeta()),
  }))
);
